import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Users, Award, TrendingUp, BookOpen, BarChart2, PieChart,
  Building2, ChevronDown, Megaphone, Send, CheckCircle2, Download,
  Activity, ShieldCheck, Flame, Lock,
} from '../components/CustomIcons';
import EmptyState from '../components/EmptyState';
import WelcomeModal from '../components/WelcomeModal';
import { useMarkOnboardingPath } from '../components/WelcomeModal';

// ── Shared primitives ────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon: Icon, accent = '#F97316' }) {
  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px' }}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{label}</span>
        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color={accent} />
        </div>
      </div>
      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '36px', color: 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: 'var(--text-dim)', marginTop: '8px' }}>{sub}</div>}
    </div>
  );
}

function SimpleBar({ label, value, maxValue, color = '#F97316' }) {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ fontSize: '13px', color: '#F97316', fontWeight: 600 }}>{value}</span>
      </div>
      <div style={{ height: '6px', background: 'var(--bg-page)', borderRadius: '3px' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '3px', transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );
}

// ── Tab sub-components ───────────────────────────────────────────────────────

function OverviewTab({ stats, analytics, onViewLearners }) {
  if (!stats) return null;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
        <StatCard label="Total Learners" value={stats.total_learners} sub="Active this week" icon={Users} />
        <StatCard label="Courses in Progress" value={stats.courses_in_progress} sub="Across all pathways" icon={BookOpen} />
        {stats.credentials_this_month === 0 ? (
          <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', overflow: 'hidden' }}>
            <EmptyState
              compact
              icon={Award}
              title="No credentials issued yet this month"
              subtitle="Learners completing pathway modules will earn credentials here."
              action={{ label: 'View nearing completion', onClick: onViewLearners }}
              testId="admin-creds-empty"
            />
          </div>
        ) : (
          <StatCard label="Credentials Issued" value={stats.credentials_this_month} sub="This month" icon={Award} />
        )}
        <StatCard label="Daily Engagement" value={`${stats.daily_engagement_rate}%`} sub="Avg login rate" icon={TrendingUp} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Total Credits Earned</div>
          <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '32px', color: '#22C55E' }}>{(stats.total_credits_earned || 0).toLocaleString()}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-dim)', marginTop: '6px' }}>Avg {(stats.avg_credits_per_learner || 0).toLocaleString()} per learner</div>
        </div>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Credits Redeemed</div>
          <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '32px', color: '#F97316' }}>{(stats.total_credits_spent || 0).toLocaleString()}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-dim)', marginTop: '6px' }}>Mostly call time &amp; commissary</div>
        </div>
        {stats.avg_lessons_per_day === 0 ? (
          <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', overflow: 'hidden' }}>
            <EmptyState
              compact
              icon={BookOpen}
              title="No lesson activity yet today"
              subtitle="Lesson completions will appear here once learners start their day."
              action={{ label: 'View learner roster', onClick: onViewLearners }}
              testId="admin-lessons-empty"
            />
          </div>
        ) : (
          <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Avg Lessons / Day</div>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '32px', color: 'var(--text-primary)' }}>{stats.avg_lessons_per_day}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-dim)', marginTop: '6px' }}>Across all active learners</div>
          </div>
        )}
      </div>
      {analytics?.top_courses && (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px' }}>
          <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BarChart2 size={16} color="#F97316" /> Top Courses by Enrollment
          </div>
          {analytics.top_courses.map(c => (
            <SimpleBar key={c.course} label={c.course} value={c.enrolled} maxValue={Math.max(...analytics.top_courses.map(x => x.enrolled))} />
          ))}
        </div>
      )}
    </>
  );
}

function LearnersTab({ users }) {
  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', overflow: 'hidden' }} data-testid="learners-table">
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #1A1A1A' }}>
        <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)' }}>Active Learners ({users.length})</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #1A1A1A' }}>
              {['Learner', 'DOC ID', 'Pathway', 'Progress', 'Credits', 'Streak', 'Release'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', color: 'var(--text-dim)', fontWeight: 700, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const profile = u.profile || {};
              const progress = profile.pathway_progress?.[profile.current_pathway] || 0;
              const releaseDate = profile.release_date;
              const daysLeft = releaseDate ? Math.max(0, Math.round((new Date(releaseDate) - new Date()) / 86400000)) : null;
              return (
                <tr key={u._id} style={{ borderBottom: '1px solid #141414' }} data-testid={`learner-row-${u._id}`}>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #F97316, #F0943A)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#111111', flexShrink: 0 }}>
                        {u.first_name?.[0]}{u.last_name?.[0]}
                      </div>
                      <div>
                        <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' }}>{u.first_name} {u.last_name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{profile.doc_id || '—'}</td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {profile.current_pathway ? profile.current_pathway.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—'}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '60px', height: '5px', background: 'var(--bg-page)', borderRadius: '3px' }}>
                        <div style={{ height: '100%', width: `${progress}%`, background: progress >= 80 ? '#22C55E' : '#F97316', borderRadius: '3px' }} />
                      </div>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{progress}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', color: '#F97316' }}>
                    {(profile.credits || 0).toLocaleString()}
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: (profile.streak_days || 0) >= 7 ? '#22C55E' : '#666666' }}>
                    {profile.streak_days || 0}d
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    {daysLeft !== null
                      ? <span style={{ fontSize: '12px', color: daysLeft < 90 ? '#EF4444' : '#666666' }}>{daysLeft}d</span>
                      : <span style={{ fontSize: '12px', color: '#333333' }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AnalyticsTab({ analytics, onExport, exporting }) {
  if (!analytics) return null;
  const completionColors = ['#22C55E', '#F97316', '#444444', '#EF4444'];
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button
          onClick={onExport}
          disabled={exporting}
          data-testid="export-analytics-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '7px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 600, opacity: exporting ? 0.6 : 1 }}>
          <Download size={13} color="#F97316" />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px' }}>
          <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Award size={16} color="#F97316" /> Credentials by Pathway
          </div>
          {analytics.credentials_by_pathway.map(c => (
            <SimpleBar key={c.pathway} label={c.pathway} value={c.count} maxValue={Math.max(...analytics.credentials_by_pathway.map(x => x.count))} />
          ))}
        </div>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px' }}>
          <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PieChart size={16} color="#F97316" /> Pathway Completion Rates
          </div>
          {analytics.completion_rates.map((c, i) => (
            <div key={c.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #1A1A1A' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: completionColors[i] }} />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{c.name}</span>
              </div>
              <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: completionColors[i] }}>{c.value}%</span>
            </div>
          ))}
        </div>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px' }}>
          <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingUp size={16} color="#F97316" /> 30-Day Engagement
          </div>
          <div style={{ display: 'flex', gap: '3px', height: '80px', alignItems: 'flex-end' }}>
            {analytics.engagement.slice(-20).map((d, i) => {
              const maxVal = Math.max(...analytics.engagement.map(x => x.learners));
              const h = Math.max(4, (d.learners / maxVal) * 80);
              const isLast = i === analytics.engagement.slice(-20).length - 1;
              return (
                <div key={d.day || i} title={`${d.day}: ${d.learners} learners`}
                  style={{ flex: 1, height: `${h}px`, background: isLast ? '#F97316' : '#333333', borderRadius: '2px', transition: 'background 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#F97316'; }}
                  onMouseLeave={e => { if (!isLast) e.currentTarget.style.background = '#333333'; }} />
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>30 days ago</span>
            <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>Today</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FacilitiesTab({ facilities }) {
  if (!facilities || facilities.length === 0) {
    return (
      <EmptyState icon={Building2} title="No facility data" subtitle="Facility comparison data is not yet available." testId="facilities-comparison-empty" />
    );
  }
  const metrics = [
    { key: 'active_learners',         label: 'Population',         suffix: '',    icon: Users,        color: '#F97316' },
    { key: 'enrolled_learners',        label: 'Enrolled',           suffix: '',    icon: BookOpen,     color: '#22C55E' },
    { key: 'daily_engagement_rate',    label: 'Daily Engagement',   suffix: '%',   icon: Activity,     color: '#3B82F6' },
    { key: 'credentials_this_month',   label: 'Creds This Month',   suffix: '',    icon: ShieldCheck,  color: '#A855F7' },
    { key: 'avg_lessons_per_day',      label: 'Avg Lessons/Day',    suffix: '',    icon: TrendingUp,   color: '#F59E0B' },
    { key: 'at_risk_count',            label: 'At-Risk Learners',   suffix: '',    icon: Flame,        color: '#EF4444' },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '18px', marginBottom: '28px' }}>
        {facilities.map(fac => (
          <div key={fac.id} style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '16px', overflow: 'hidden' }}
            data-testid={`facility-card-${fac.id}`}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #141414', background: 'rgba(249,115,22,0.04)' }}>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)' }}>{fac.short_name}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '3px' }}>{fac.location}</div>
              <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '11px', background: 'rgba(249,115,22,0.12)', color: '#F97316', borderRadius: '6px', padding: '2px 8px', fontWeight: 600 }}>
                  Capacity {fac.capacity?.toLocaleString()}
                </span>
              </div>
            </div>
            <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              {metrics.map(m => (
                <div key={m.key}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '4px' }}>
                    <m.icon size={11} color={m.color} />
                    <span style={{ fontSize: '10px', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{m.label}</span>
                  </div>
                  <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '22px', color: m.key === 'at_risk_count' && fac[m.key] > 0 ? '#EF4444' : 'var(--text-primary)' }}>
                    {fac[m.key]?.toLocaleString?.() ?? fac[m.key]}{m.suffix}
                  </div>
                </div>
              ))}
            </div>
            {/* Engagement bar */}
            <div style={{ padding: '0 20px 16px' }}>
              <div style={{ height: '4px', background: 'var(--bg-page)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(fac.daily_engagement_rate, 100)}%`, background: '#F97316', borderRadius: '2px', transition: 'width 0.6s ease' }} />
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-dim)', marginTop: '4px' }}>{fac.daily_engagement_rate}% daily engagement today</div>
            </div>
          </div>
        ))}
      </div>

      {/* Comparison table */}
      <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', overflow: 'auto' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #141414', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)' }}>
          Side-by-Side Comparison
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'rgba(249,115,22,0.04)' }}>
              <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textAlign: 'left' }}>Metric</th>
              {facilities.map(f => (
                <th key={f.id} style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', textAlign: 'center' }}>{f.short_name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map(m => (
              <tr key={m.key} style={{ borderTop: '1px solid #141414' }}>
                <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <m.icon size={13} color={m.color} /> {m.label}
                </td>
                {facilities.map(f => (
                  <td key={f.id} style={{ padding: '12px 16px', textAlign: 'center', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: m.key === 'at_risk_count' && f[m.key] > 0 ? '#EF4444' : 'var(--text-primary)' }}>
                    {f[m.key]?.toLocaleString?.() ?? f[m.key]}{m.suffix}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AnnounceTab({ announce, setAnnounce, sendStatus, onSend }) {
  return (
    <div style={{ maxWidth: '600px' }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '16px', padding: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          <Megaphone size={18} color="#F97316" />
          <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '18px', color: 'var(--text-primary)', margin: 0 }}>
            Send Announcement
          </h2>
        </div>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 24px 0', lineHeight: 1.5 }}>
          Deliver a message to all learners instantly — appears in their notification bell and triggers a push notification for those who have it enabled.
        </p>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Subject
          </label>
          <input className="input-dark" placeholder="e.g. New pathway now available: Solar PV!"
            value={announce.title} onChange={e => setAnnounce(p => ({ ...p, title: e.target.value }))}
            maxLength={80} data-testid="announce-title-input" />
          <div style={{ textAlign: 'right', fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px' }}>{announce.title.length}/80</div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Message
          </label>
          <textarea className="input-dark" placeholder="Write your message here..."
            value={announce.message} onChange={e => setAnnounce(p => ({ ...p, message: e.target.value }))}
            rows={4} maxLength={500} data-testid="announce-message-input"
            style={{ resize: 'vertical', minHeight: '100px', fontFamily: 'Inter, sans-serif' }} />
          <div style={{ textAlign: 'right', fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px' }}>{announce.message.length}/500</div>
        </div>

        {sendStatus === 'sent' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}
            data-testid="announce-success">
            <CheckCircle2 size={16} style={{ color: '#22C55E' }} />
            <span style={{ fontSize: '13px', color: '#22C55E', fontWeight: 600 }}>Announcement sent to all learners!</span>
          </div>
        )}
        {sendStatus === 'error' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}>
            <span style={{ fontSize: '13px', color: '#EF4444', fontWeight: 600 }}>Failed to send. Please try again.</span>
          </div>
        )}

        <button className="btn-primary" onClick={onSend}
          disabled={!announce.title.trim() || !announce.message.trim() || sendStatus === 'sending'}
          data-testid="send-announce-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', opacity: (!announce.title.trim() || !announce.message.trim()) ? 0.5 : 1 }}>
          <Send size={14} />
          {sendStatus === 'sending' ? 'Sending...' : 'Send to All Learners'}
        </button>
        <p style={{ fontSize: '12px', color: 'var(--text-dim)', marginTop: '12px' }}>
          This will reach all 5 learners across all facilities.
        </p>
      </div>
    </div>
  );
}

// ── Main page component (~90 lines) ─────────────────────────────────────────

const TABS = ['overview', 'learners', 'analytics', 'facilities', 'announce'];

export default function Admin() {
  const { authFetch, user } = useAuth();
  const facilityScope = user?.profile?.facility_scope || '';

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [facilitiesComparison, setFacilitiesComparison] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(facilityScope);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [announce, setAnnounce] = useState({ title: '', message: '' });
  const [sendStatus, setSendStatus] = useState(null);

  useMarkOnboardingPath('/admin');

  const fetchData = (facility = '') => {
    setLoading(true);
    const q = facility ? `?facility=${encodeURIComponent(facility)}` : '';
    const usersQ = facility ? `?role=learner&facility=${encodeURIComponent(facility)}` : '?role=learner';
    Promise.all([
      authFetch(`/admin/stats${q}`),
      authFetch(`/admin/users${usersQ}`),
      authFetch(`/admin/analytics${q}`),
    ]).then(([sR, uR, aR]) => {
      setStats(sR.data);
      setUsers(uR.data);
      setAnalytics(aR.data);
    }).catch(e => console.error('Admin fetch error:', e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!facilityScope) {
      authFetch('/admin/facilities')
        .then(r => setFacilities(r.data || []))
        .catch(e => console.error('Facilities fetch error:', e));
    }
    authFetch('/admin/facilities/comparison')
      .then(r => setFacilitiesComparison(r.data || []))
      .catch(e => console.error('Facilities comparison fetch error:', e));
    fetchData(facilityScope);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFacilityChange = (f) => {
    if (facilityScope) return; // scoped admin: locked
    setSelectedFacility(f);
    fetchData(f);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const q = selectedFacility ? `?facility=${encodeURIComponent(selectedFacility)}` : '';
      const resp = await authFetch(`/admin/analytics/export${q}`, { responseType: 'blob' });
      const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedFacility ? `AssistivOS_Analytics_${selectedFacility}.csv` : 'AssistivOS_Analytics.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export error:', e);
    } finally {
      setExporting(false);
    }
  };

  const sendAnnouncement = async () => {
    if (!announce.title.trim() || !announce.message.trim()) return;
    setSendStatus('sending');
    try {
      await authFetch('/notifications/broadcast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { title: announce.title.trim(), message: announce.message.trim() },
      });
      setSendStatus('sent');
      setAnnounce({ title: '', message: '' });
      setTimeout(() => setSendStatus(null), 3500);
    } catch (e) {
      console.error('Broadcast error:', e);
      setSendStatus('error');
      setTimeout(() => setSendStatus(null), 3500);
    }
  };

  if (loading) return <div style={{ padding: '40px', color: 'var(--text-secondary)', textAlign: 'center' }}>Loading dashboard...</div>;

  return (
    <div style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="admin-dashboard">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header + Facility Selector */}
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>
            {facilityScope ? `${facilityScope.split(' ').map(w => w[0]).join('').slice(0,5)} Admin Dashboard` : 'Admin Dashboard'}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
            {facilityScope ? (
              // Scoped admin: locked indicator — cannot switch facilities
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', background: 'var(--bg-elevated)', border: '1px solid rgba(249,115,22,0.25)', borderRadius: '8px', padding: '6px 12px' }}
                data-testid="facility-scope-locked">
                <Lock size={12} color="#F97316" />
                <span style={{ fontSize: '13px', color: '#F97316', fontWeight: 600 }}>{facilityScope}</span>
              </div>
            ) : (
              // Super admin: full dropdown
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
                <Building2 size={14} style={{ color: 'var(--text-muted)' }} />
                <select value={selectedFacility} onChange={e => handleFacilityChange(e.target.value)}
                  data-testid="facility-selector"
                  style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: '8px', color: 'var(--text-secondary)', fontSize: '13px', padding: '5px 32px 5px 10px', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}>
                  <option value="">All Facilities</option>
                  {facilities.map(f => (
                    <option key={f.id} value={f.name}>{f.short_name} — {f.location}</option>
                  ))}
                </select>
                <ChevronDown size={12} style={{ position: 'absolute', right: '10px', color: 'var(--text-muted)', pointerEvents: 'none' }} />
              </div>
            )}
            {selectedFacility && !facilityScope && (
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Showing data for: <strong style={{ color: 'var(--orange)' }}>{selectedFacility}</strong>
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-surface)', borderRadius: '10px', padding: '4px', marginBottom: '24px', width: 'fit-content', flexWrap: 'wrap' }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: 'none', background: tab === t ? '#F97316' : 'transparent', color: tab === t ? '#111111' : '#666666', fontFamily: 'Plus Jakarta Sans, sans-serif', textTransform: 'capitalize' }}
              data-testid={`admin-tab-${t}`}>
              {t === 'announce' ? 'Announce' : t}
            </button>
          ))}
        </div>

        {tab === 'overview'   && <OverviewTab stats={stats} analytics={analytics} onViewLearners={() => setTab('learners')} />}
        {tab === 'learners'   && <LearnersTab users={users} />}
        {tab === 'analytics'  && <AnalyticsTab analytics={analytics} onExport={handleExport} exporting={exporting} />}
        {tab === 'facilities' && <FacilitiesTab facilities={facilitiesComparison} />}
        {tab === 'announce'   && <AnnounceTab announce={announce} setAnnounce={setAnnounce} sendStatus={sendStatus} onSend={sendAnnouncement} />}
      </div>
      <WelcomeModal />
    </div>
  );
}
